/**
 * React Query hook returning every component rule for the current
 * claimflow scope, optionally filtered by component id and/or status.
 *
 * Distinct from {@link useComponentRulesList} (which takes a required
 * `componentId`): this hook powers the module-scoped raw-JSON CRUD
 * surface on the admin module edit page, where the caller wants to see
 * every rule at once. The query key is kept separate from the
 * per-component list so the two views do not clobber each other's
 * cache entries.
 *
 * `staleTime` is 30 seconds — the module edit page will typically be
 * open for a few minutes while an admin tweaks rules, and 30s is the
 * smallest value that still avoids a refetch on every navigation between
 * the modal and the table.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { RuleStatus } from '@components/shared/configurable';

import {
  componentRulesService,
  type ComponentRuleDTO,
} from '../../api/componentRulesService';
import { componentRulesKeys } from './queryKeys';

/**
 * Options accepted by `useAllComponentRulesList`.
 *
 * Both filters are optional. Omitting `componentId` retrieves every
 * rule the backend is willing to return for the caller's tenant; the
 * `status` filter works the same way.
 */
export interface UseAllComponentRulesListOptions {
  /** Optional component id filter. Omit to retrieve every component's rules. */
  componentId?: string;
  /** Optional lifecycle filter (draft/active/archived). */
  status?: RuleStatus;
}

/**
 * Fetch every rule under the current claimflow scope, optionally
 * narrowed by component id and/or status.
 *
 * Returns raw DTOs (snake_case). Consumers that need the camelCased
 * `ComponentRule` shape should map results via
 * `mapComponentRuleFromDTO` from the service file.
 */
export function useAllComponentRulesList(
  options: UseAllComponentRulesListOptions = {},
): UseQueryResult<ComponentRuleDTO[]> {
  return useQuery<ComponentRuleDTO[]>({
    queryKey: componentRulesKeys.listAll(options.componentId, options.status),
    queryFn: () =>
      componentRulesService.listAllRules({
        componentId: options.componentId,
        status: options.status,
      }),
    staleTime: 30_000,
  });
}
